import chroma from 'chroma-js';
import { clamp } from 'lodash-es';
import type { ColorBin, Colormap, ColormapId, RampStop } from './types';

/** Ramp used when a map has no default of its own. */
export const DEFAULT_COLORMAP_ID: ColormapId = 'viridis';

/**
 * The built-in ramps, in the order the colormap picker lists them: the design
 * system's Ramp Swatch profiles (sim-components colorRamps.ts), each a handful of
 * hand-chosen stops so the editor shows exactly what gets painted.
 */
export const COLORMAPS: Colormap[] = [
  {
    id: 'magma',
    label: 'Magma',
    stops: [
      { offset: 0, color: '#000004' },
      { offset: 25, color: '#51106e' },
      { offset: 50, color: '#b63679' },
      { offset: 75, color: '#fb8869' },
      { offset: 100, color: '#fcfbbf' },
    ],
  },
  {
    id: 'inferno',
    label: 'Inferno',
    stops: [
      { offset: 0, color: '#000004' },
      { offset: 25, color: '#57106e' },
      { offset: 50, color: '#bc375a' },
      { offset: 75, color: '#f98e09' },
      { offset: 100, color: '#fcffb7' },
    ],
  },
  {
    id: 'plasma',
    label: 'Plasma',
    stops: [
      { offset: 0, color: '#0d0887' },
      { offset: 25, color: '#7e03a8' },
      { offset: 50, color: '#cc477a' },
      { offset: 75, color: '#f89441' },
      { offset: 100, color: '#f0f921' },
    ],
  },
  {
    id: 'viridis',
    label: 'Viridis',
    stops: [
      { offset: 0, color: '#440154' },
      { offset: 25, color: '#3b528b' },
      { offset: 50, color: '#219a8c' },
      { offset: 75, color: '#5ec962' },
      { offset: 100, color: '#fde725' },
    ],
  },
  {
    id: 'cividis',
    label: 'Cividis',
    stops: [
      { offset: 0, color: '#00224e' },
      { offset: 25, color: '#36456c' },
      { offset: 50, color: '#6c6e72' },
      { offset: 75, color: '#a5a05a' },
      { offset: 100, color: '#ffea46' },
    ],
  },
  {
    id: 'twilight',
    label: 'Twilight',
    stops: [
      { offset: 0, color: '#e2d9e2' },
      { offset: 20, color: '#86a6c2' },
      { offset: 40, color: '#4d1c6b' },
      { offset: 60, color: '#861657' },
      { offset: 80, color: '#b78768' },
      { offset: 100, color: '#e2d9e2' },
    ],
  },
  {
    id: 'twilightShifted',
    label: 'Twilight Shifted',
    stops: [
      { offset: 0, color: '#4d1c6b' },
      { offset: 20, color: '#861657' },
      { offset: 40, color: '#b78768' },
      { offset: 60, color: '#e2d9e2' },
      { offset: 80, color: '#86a6c2' },
      { offset: 100, color: '#4d1c6b' },
    ],
  },
  {
    id: 'turbo',
    label: 'Turbo',
    stops: [
      { offset: 0, color: '#301d9e' },
      { offset: 20, color: '#277ebc' },
      { offset: 40, color: '#1abd9a' },
      { offset: 60, color: '#a7d83f' },
      { offset: 80, color: '#f89540' },
      { offset: 100, color: '#c4262e' },
    ],
  },
  {
    id: 'blues',
    label: 'Blues',
    stops: [
      { offset: 0, color: '#f7fbff' },
      { offset: 25, color: '#c6dbef' },
      { offset: 50, color: '#6baed6' },
      { offset: 75, color: '#2171b5' },
      { offset: 100, color: '#08306b' },
    ],
  },
  {
    id: 'brbg',
    label: 'BrBG',
    stops: [
      { offset: 0, color: '#543200' },
      { offset: 25, color: '#bf812d' },
      { offset: 50, color: '#f5f5f5' },
      { offset: 75, color: '#80cdc1' },
      { offset: 100, color: '#003641' },
    ],
  },
  {
    id: 'bugn',
    label: 'BuGn',
    stops: [
      { offset: 0, color: '#f7fcfd' },
      { offset: 25, color: '#ccece6' },
      { offset: 50, color: '#66c2a4' },
      { offset: 75, color: '#2ca25f' },
      { offset: 100, color: '#00441b' },
    ],
  },
  {
    id: 'bupu',
    label: 'BuPu',
    stops: [
      { offset: 0, color: '#f7fcfd' },
      { offset: 25, color: '#cddaeb' },
      { offset: 50, color: '#8c96c6' },
      { offset: 75, color: '#88419d' },
      { offset: 100, color: '#4d004b' },
    ],
  },
  {
    id: 'cmrmap',
    label: 'CMRmap',
    stops: [
      { offset: 0, color: '#000000' },
      { offset: 18, color: '#1f2c8a' },
      { offset: 36, color: '#bf2c7f' },
      { offset: 55, color: '#ff3f1f' },
      { offset: 75, color: '#ffc800' },
      { offset: 100, color: '#ffffff' },
    ],
  },
  {
    id: 'gnbu',
    label: 'GnBu',
    stops: [
      { offset: 0, color: '#f7fcf0' },
      { offset: 25, color: '#c7e9c0' },
      { offset: 50, color: '#7fcdbb' },
      { offset: 75, color: '#41b6c4' },
      { offset: 100, color: '#085289' },
    ],
  },
  {
    id: 'greens',
    label: 'Greens',
    stops: [
      { offset: 0, color: '#f7fcf5' },
      { offset: 25, color: '#c7e9c0' },
      { offset: 50, color: '#74c476' },
      { offset: 75, color: '#238b45' },
      { offset: 100, color: '#00441b' },
    ],
  },
];

const COLORMAPS_BY_ID = new Map<ColormapId, Colormap>(
  COLORMAPS.map((colormap) => [colormap.id, colormap])
);

/**
 * Looks up a built-in ramp, falling back to the default when the id is unknown
 * (for example a ramp id stored by a newer release).
 * @param {string | null | undefined} id The ramp id, possibly unknown.
 * @return {Colormap} The matching ramp, or Viridis.
 */
export function getColormap(id: string | null | undefined): Colormap {
  // The map is keyed by ColormapId; an unknown string simply misses, so widening the
  // lookup key is safe. The default id is in COLORMAPS, so the fallback always exists.
  return (
    COLORMAPS_BY_ID.get(id as ColormapId) ??
    (COLORMAPS_BY_ID.get(DEFAULT_COLORMAP_ID) as Colormap)
  );
}

/**
 * A built-in ramp's editable stops; reversed, each stop mirrors to 100 - offset.
 * @param {ColormapId} id The ramp id.
 * @param {boolean} reversed Whether to run the ramp high-to-low.
 * @return {RampStop[]} The ramp's stops, low end first.
 */
export function colormapStops(id: ColormapId, reversed = false): RampStop[] {
  const { stops } = getColormap(id);
  if (!reversed) return stops.map((stop) => ({ ...stop }));
  return [...stops]
    .reverse()
    .map((stop) => ({ ...stop, offset: 100 - stop.offset }));
}

/**
 * Samples a ramp at one position, interpolating between the stops either side.
 * Stops need not be sorted; positions outside 0–100 take the end colour.
 * @param {RampStop[]} stops The ramp's stops (at least one).
 * @param {number} position Where to sample, 0–100.
 * @return {string} The colour as a hex string.
 * @throws {Error} When `stops` is empty.
 */
export function sampleRamp(stops: RampStop[], position: number): string {
  return rampScale(stops)(position).hex('rgb');
}

/**
 * Builds a reusable sampler for a ramp, so callers that sample many positions
 * (the colour table) sort the stops and build the scale once.
 * @param {RampStop[]} stops The ramp's stops (at least one).
 * @return {Function} Maps a 0–100 position (clamped) to a chroma colour.
 * @throws {Error} When `stops` is empty.
 */
function rampScale(stops: RampStop[]): (position: number) => chroma.Color {
  if (stops.length === 0) throw new Error('A ramp needs at least one stop');
  const sorted = [...stops].sort((a, b) => a.offset - b.offset);
  const scale = chroma
    .scale(sorted.map((stop) => stop.color))
    .domain(sorted.map((stop) => stop.offset))
    .mode('rgb');
  return (position) => scale(clamp(position, 0, 100));
}

/**
 * Splits a value range into equal bins and gives each the colour the map paints
 * it with, for the colour tab's bin list.
 * @param {number} min Low end of the range.
 * @param {number} max High end of the range.
 * @param {RampStop[]} stops The ramp's stops.
 * @param {number} binCount Number of bins.
 * @return {ColorBin[]} One entry per bin, low end first.
 */
export function colorBins(
  min: number,
  max: number,
  stops: RampStop[],
  binCount: number
): ColorBin[] {
  const bins = Math.max(1, Math.round(binCount));
  const width = (max - min) / bins;
  const sample = rampScale(stops);
  return Array.from({ length: bins }, (_, index) => ({
    x0: min + index * width,
    x1: index === bins - 1 ? max : min + (index + 1) * width,
    color: sample(((index + 0.5) / bins) * 100).hex('rgb'),
  }));
}

/**
 * A CSS linear-gradient (left to right) through a ramp's stops, for ramp bars
 * and ramp previews.
 * @param {RampStop[]} stops The ramp's stops, in any order.
 * @return {string} The CSS background value.
 */
export function rampGradientCss(stops: RampStop[]): string {
  const sorted = [...stops].sort((a, b) => a.offset - b.offset);
  return `linear-gradient(to right, ${sorted
    .map((stop) => `${stop.color} ${stop.offset}%`)
    .join(', ')})`;
}
